/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { QueryObserverResult, useQuery } from 'react-query';
import {
	participantsAdminuiGeoareaApi,
	participantsAdminuiTenantApi,
	sharedAdminuiAppApi
} from '../../../../ServiceFactory';
import { ClientAppVariant, ClientGeoAreaExtended, ClientTenant } from '@DigitaleDoerfer/digitale-doerfer-api';
import { Contract, ListType } from '../../types';

export type ItemDetails = {
	[key: string]: string | string[][] | undefined;
	appVariantId: string | undefined;
	geoAreaIdsExcluded: string[][];
	geoAreaIdsIncluded: string[][];
	tenantId: string | undefined;
};

const useFetchAppVariant = (appVariantId: string | undefined): QueryObserverResult<ClientAppVariant> => {
	return useQuery<ClientAppVariant>(
		[ListType.APP_VARIANT, { appVariantId }],
		() =>
			sharedAdminuiAppApi()
				.getAppVariantById({ appVariantId: appVariantId ?? '' })
				.then(response => response),
		{
			enabled: !!appVariantId
		}
	);
};
const useFetchTenant = (tenantId: string | undefined): QueryObserverResult<ClientTenant> => {
	return useQuery<ClientTenant>(
		[ListType.TENANT, { tenantId }],
		() =>
			participantsAdminuiTenantApi()
				.getTenantById({ tenantId: tenantId ?? '' })
				.then(response => response),
		{
			enabled: !!tenantId
		}
	);
};
const useFetchGeoArea = (geoAreaIds: string[] | undefined): QueryObserverResult<ClientGeoAreaExtended[]> => {
	return useQuery(['ClientGeoAreaExtended', { geoAreaIds }], () =>
		participantsAdminuiGeoareaApi()
			.getGeoAreaWithChildren({ geoAreaIds, depth: 0, revealParentGeoAreas: true })
			.then(response => {
				if (geoAreaIds === undefined || geoAreaIds.length < 1) return [];
				return (
					response?.map(item => {
						return item as ClientGeoAreaExtended;
					}) || []
				);
			})
	);
};

const getGeoAreaString = (geoAreas: ClientGeoAreaExtended[]): string[][] => {
	const paths = (area: ClientGeoAreaExtended | undefined): string[][] => {
		if (area?.name && area?.childGeoAreas) {
			return area.childGeoAreas.flatMap(paths).map(r => [area.name + ' > ' ?? '', ...r]);
		}
		return [[`${area?.name ?? ''} ${area?.id ? `(Id: ${area.id})` : ''}`]];
	};
	return paths(geoAreas[0]);
};

const useItemDetails = (item: Contract | null): ItemDetails => {
	const appVariant = useFetchAppVariant(item?.appVariantId);
	const geoAreasExcluded = useFetchGeoArea(item?.geoAreaIdsExcluded);
	const geoAreasIncluded = useFetchGeoArea(item?.geoAreaIdsIncluded);
	const tenant = useFetchTenant(item?.tenantId);

	return {
		appVariantId: appVariant?.data?.name,
		geoAreaIdsExcluded: geoAreasExcluded?.data ? getGeoAreaString(geoAreasExcluded.data) : [],
		geoAreaIdsIncluded: geoAreasIncluded?.data !== undefined ? getGeoAreaString(geoAreasIncluded.data) : [],
		tenantId: tenant?.data?.name
	};
};

export default useItemDetails;
